// The shape of `getAnalyticsResult()` — confirmed real, not guessed, by
// reading the Cashflow app's own production code (`src/types.ts` /
// `src/lib/indexers.ts` in `gitlab.com/giraffe-build/cashflow`), since no tool
// available while building this could get the RPC's own result (a native
// `Set` buried inside it) to cross back here directly.
//
// Pure data — no Giraffe SDK import, same boundary as data/types.ts. The SDK
// side (giraffe/analyticsResult.ts) does the actual RPC call and re-exports
// these; data/analyticsTable.ts consumes them without pulling the SDK in.

export interface AnalyticsComponent {
  id?: string | null;
  type?: string | null;
  categoryId?: string | null;
  subType?: string | null;
  unitId?: string | null;
}

export interface AnalyticsMeasure {
  id: string;
  name: string;
  formula?: string | null;
  subType?: string | null;
  components?: AnalyticsComponent[];
  unitId?: string | null;
  categoryId?: string | null;
  isBold?: boolean;
  isHidden?: boolean;
}

export interface AnalyticsColumn {
  /** The group-by value this column is for — a layerId, a scenario id, … `undefined` means the combined total. */
  groupKey?: string;
  groupName?: string;
  groupLabel?: string;
  name?: string;
  label?: string;
  title?: string;
  value?: number | null;
}

export interface AnalyticsRow {
  measure?: AnalyticsMeasure;
  columns?: AnalyticsColumn[];
}

export interface AnalyticsUnitTotalItem {
  pronumeral?: string;
  label: string;
  value?: number;
}

export interface AnalyticsCombined {
  rows?: AnalyticsRow[];
  unitTotals?: Record<string, AnalyticsUnitTotalItem[]>;
}

export interface AnalyticsGroup {
  label?: string;
  tabs?: string[];
  usages?: { __COMBINED?: AnalyticsCombined };
}

export interface AnalyticsDataRoot {
  grouped?: Record<string, AnalyticsGroup>;
}

/** Every row across every category, flattened — the shape every consumer below actually wants. */
export function analyticsRows(data: AnalyticsDataRoot | null): AnalyticsRow[] {
  if (!data?.grouped) return [];
  return Object.values(data.grouped).flatMap((g) => g.usages?.__COMBINED?.rows ?? []);
}

/** The label a column's header should show — "All" for the combined total, matching Cashflow's own convention. */
export function columnLabel(column: AnalyticsColumn): string {
  const raw = column.groupLabel ?? column.groupName ?? column.label ?? column.name ?? column.title ?? column.groupKey;
  return !raw || String(raw) === '__COMBINED' ? 'All' : String(raw);
}

/** The key a column is for — `"__COMBINED"` when it names none, same fallback as Cashflow's `indexValues`. */
export function columnKey(column: AnalyticsColumn): string {
  return column.groupKey ?? '__COMBINED';
}
